import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';

import Loading from '../../../components/common/loading';
import TextInput from '../../../components/common/inputs/textInput.jsx';
import SelectInput from '../../../components/common/inputs/selectInput.jsx';
import NotificationManager from '../../../utils/notificationManager';
import ModalManager from '../../../components/common/modal/ModalManager';

const styles = {
  wrapper: {
    background: '#ffffff',
    borderRadius: 8,
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    padding: '24px',
    minHeight: '320px',
  },
  header: {
    marginBottom: '16px',
  },
  title: {
    margin: 0,
    fontSize: '20px',
    fontWeight: 600,
    color: '#1f2d3d',
  },
  description: {
    marginTop: '8px',
    color: '#607284',
    fontSize: '14px',
    lineHeight: 1.6,
  },
  content: {
    display: 'grid',
    gridTemplateColumns: 'minmax(220px, 260px) 1fr',
    gap: '24px',
  },
  listCard: {
    background: '#f9fafb',
    borderRadius: 8,
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  listHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  listTitle: {
    margin: 0,
    fontSize: '15px',
    fontWeight: 600,
    color: '#1f2d3d',
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    maxHeight: '420px',
    overflowY: 'auto',
  },
  listItem: {
    padding: '10px 12px',
    borderRadius: 6,
    border: '1px solid transparent',
    background: '#ffffff',
    color: '#1f2d3d',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    transition: 'all 0.2s ease',
  },
  listItemActive: {
    borderColor: '#3b82f6',
    background: 'rgba(59,130,246,0.1)',
  },
  listItemTitle: {
    fontWeight: 600,
    fontSize: '14px',
  },
  listItemSubtitle: {
    fontSize: '12px',
    color: '#607284',
  },
  createButton: {
    padding: '10px 12px',
    borderRadius: 6,
    border: '1px dashed #cbd5f5',
    background: 'rgba(59,130,246,0.08)',
    color: '#1f4ed8',
    cursor: 'pointer',
    fontWeight: 600,
    textAlign: 'center',
  },
  createButtonDisabled: {
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  detailCard: {
    background: '#f9fafb',
    borderRadius: 8,
    padding: '20px 24px',
    minHeight: '320px',
  },
  detailHeader: {
    marginBottom: '16px',
  },
  detailTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#1f2d3d',
  },
  detailSubtitle: {
    marginTop: '6px',
    color: '#607284',
    fontSize: '13px',
  },
  section: {
    marginTop: '20px',
    paddingTop: '20px',
    borderTop: '1px solid #e2e8f0',
  },
  buttonRow: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    marginTop: '20px',
    alignItems: 'center',
  },
  dangerButton: {
    background: '#b91c1c',
    color: '#ffffff',
  },
  secondaryButton: {
    background: '#e2e8f0',
    color: '#1f2937',
  },
  requirementList: {
    listStyle: 'none',
    margin: '12px 0 0',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  requirementItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: '#64748b',
  },
  requirementMet: {
    color: '#047857',
  },
  requirementIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    background: '#e2e8f0',
    fontSize: '11px',
    fontWeight: 600,
    color: '#475569',
  },
  requirementIconMet: {
    background: '#bbf7d0',
    color: '#047857',
  },
  message: {
    padding: '24px',
    textAlign: 'center',
    color: '#607284',
    background: '#ffffff',
    borderRadius: 8,
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  },
  errorText: {
    color: '#b91c1c',
    fontSize: '13px',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '12px',
    marginTop: '12px',
  },
  infoBox: {
    background: '#ffffff',
    borderRadius: 6,
    padding: '10px 12px',
    border: '1px solid #e2e8f0',
  },
  infoLabel: {
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    color: '#64748b',
    marginBottom: '4px',
  },
  infoValue: {
    fontSize: '14px',
    color: '#1f2d3d',
    fontWeight: 600,
  },
  listLoading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '24px 0',
    gap: '8px',
    color: '#607284',
  },
};

const passwordRequirements = [
  {
    key: 'lowercase',
    label: '1 lowercase characters required',
    test: /[a-z]/,
  },
  {
    key: 'uppercase',
    label: '1 uppercase characters required',
    test: /[A-Z]/,
  },
  {
    key: 'number',
    label: '1 number characters required',
    test: /[0-9]/,
  },
  {
    key: 'special',
    label: '1 special characters required',
    test: /[^A-Za-z0-9]/,
  },
];

const getPasswordValidationMessage = (password) => {
  if (!password) {
    return null;
  }

  const missing = passwordRequirements
    .filter((requirement) => !requirement.test.test(password))
    .map((requirement) => requirement.label);

  if (!missing.length) {
    return null;
  }

  return `Password must include: ${missing.join(', ')}`;
};

const PasswordRequirementsList = ({ password }) => {
  const currentValue = password || '';

  return (
    <ul style={styles.requirementList}>
      {passwordRequirements.map((requirement) => {
        const satisfied = requirement.test.test(currentValue);
        return (
          <li
            key={requirement.key}
            style={{
              ...styles.requirementItem,
              ...(satisfied ? styles.requirementMet : {}),
            }}
          >
            <span
              style={{
                ...styles.requirementIcon,
                ...(satisfied ? styles.requirementIconMet : {}),
              }}
            >
              {satisfied ? '✓' : '•'}
            </span>
            <span>{requirement.label}</span>
          </li>
        );
      })}
    </ul>
  );
};

const DetailSkeleton = () => {
  const skeletonLine = (style = {}) => (
    <div
      className="skeleton-blank"
      style={{
        width: '100%',
        height: '38px',
        borderRadius: '6px',
        ...style,
      }}
    />
  );

  const skeletonLabel = (style = {}) => (
    <div
      className="skeleton-blank"
      style={{
        width: '45%',
        height: '14px',
        borderRadius: '999px',
        marginBottom: '8px',
        ...style,
      }}
    />
  );

  return (
    <div style={styles.detailCard}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div>
          {skeletonLabel({ width: '35%', height: '20px', marginBottom: '12px' })}
          {skeletonLine({ width: '70%', height: '18px' })}
        </div>
        <div>
          {skeletonLabel()}
          {skeletonLine()}
        </div>
        <div>
          {skeletonLabel()}
          {skeletonLine()}
        </div>
        <div>
          {skeletonLabel({ width: '55%' })}
          {skeletonLine()}
        </div>
        <div>
          {skeletonLabel({ width: '55%' })}
          {skeletonLine()}
        </div>
      </div>
    </div>
  );
};

const formatTimestamp = (value) => {
  if (!value) {
    return '—';
  }
  try {
    return format(value * 1000, 'MM/dd/yy HH:mm');
  } catch (error) {
    return '—';
  }
};

const mapUserToForm = (user) => ({
  username: user?.username || '',
  roleId: user?.roleId ? String(user.roleId) : '',
});

export default function DeviceConfigurationUsers({ section, device, user, deviceOffline, offlineReason }) {
  const serial = device?.enclosureSerialNumber;
  const permissions = user?.role?.permissions || {};
  const rawModify = permissions.modifyUsers ?? user?.role?.modifyUsers ?? user?.role?.editDeviceInfo;
  const rawView = permissions.viewUsersList ?? user?.role?.viewUsersList;
  const canModifyPermission = rawModify !== false;
  const canModify = canModifyPermission && !deviceOffline;
  const canView = canModifyPermission || rawView !== false;

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [usersLoadError, setUsersLoadError] = useState('');
  const [rolesLoadError, setRolesLoadError] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [formState, setFormState] = useState({ username: '', roleId: '' });
  const [initialFormState, setInitialFormState] = useState(null);
  const [passwordState, setPasswordState] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const authHeaders = useMemo(() => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  }), []);

  const fetchRoles = useCallback(async () => {
    if (!serial) return;
    try {
      setLoadingRoles(true);
      const { data } = await axios.get(`/v1/devices/${serial}/roles`, {
        headers: authHeaders,
      });
      setRoles(data?.data || []);
      setRolesLoadError('');
    } catch (error) {
      console.error(error);
      setRolesLoadError('Unable to load device roles.');
      NotificationManager.alert('Failed to load device roles.');
    } finally {
      setLoadingRoles(false);
    }
  }, [serial, authHeaders]);

  const fetchUsers = useCallback(async () => {
    if (!serial) return;
    try {
      setLoadingUsers(true);
      const { data } = await axios.get(`/v1/devices/${serial}/users`, {
        headers: authHeaders,
      });
      const userList = data?.data || [];
      setUsers(userList);
      setUsersLoadError('');
      setSelectedUserId((prev) => {
        if (prev === 'new') {
          return prev;
        }
        if (prev) {
          const stillExists = userList.find((u) => String(u.id) === String(prev));
          if (stillExists) {
            return String(prev);
          }
        }
        return userList.length ? String(userList[0].id) : null;
      });
    } catch (error) {
      console.error(error);
      setUsersLoadError('Unable to load device users.');
      NotificationManager.alert('Failed to load device users.');
    } finally {
      setLoadingUsers(false);
    }
  }, [serial, authHeaders]);

  useEffect(() => {
    setUsers([]);
    setRoles([]);
    setSelectedUserId(null);
    setFormState({ username: '', roleId: '' });
    setInitialFormState(null);
    setPasswordState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setFormErrors({});
    setUsersLoadError('');
    setRolesLoadError('');

    if (serial && canView) {
      fetchRoles();
      fetchUsers();
    }
  }, [serial, canView, fetchRoles, fetchUsers]);

  const selectedUser = useMemo(
    () => users.find((u) => String(u.id) === String(selectedUserId)) || null,
    [users, selectedUserId]
  );

  const isNewUser = selectedUserId === 'new';

  useEffect(() => {
    if (isNewUser) {
      setFormState((prev) => ({
        username: '',
        roleId: prev.roleId || '',
      }));
      setInitialFormState(null);
      setPasswordState({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setFormErrors({});
      return;
    }

    if (selectedUser) {
      const mapped = mapUserToForm(selectedUser);
      setFormState(mapped);
      setInitialFormState(mapped);
      setPasswordState({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setFormErrors({});
    }
  }, [isNewUser, selectedUser, selectedUserId]);

  useEffect(() => {
    if (isNewUser && !formState.roleId && roles.length === 1) {
      setFormState((prev) => ({ ...prev, roleId: String(roles[0].id) }));
    }
  }, [isNewUser, roles, formState.roleId]);

  const roleOptions = useMemo(
    () => roles.map((role) => ({ label: role.name, value: String(role.id) })),
    [roles]
  );

  const newPasswordValidationMessage = useMemo(
    () => getPasswordValidationMessage(passwordState.newPassword),
    [passwordState.newPassword]
  );

  const listContent = () => {
    if (loadingUsers) {
      return (
        <div style={styles.listLoading}>
          <Loading size="24px" />
          <span>Loading device users…</span>
        </div>
      );
    }

    if (!users.length) {
      return (
        <div style={styles.message}>
          No device users found. Create a new user to get started.
        </div>
      );
    }

    return (
      <ul style={styles.list}>
        {users.map((deviceUser) => {
          const isActive = String(deviceUser.id) === String(selectedUserId);
          return (
            <li
              key={deviceUser.id}
              style={{
                ...styles.listItem,
                ...(isActive ? styles.listItemActive : {}),
              }}
              onClick={() => setSelectedUserId(String(deviceUser.id))}
            >
              <span style={styles.listItemTitle}>{deviceUser.username}</span>
              <span style={styles.listItemSubtitle}>
                {roles.find((r) => r.id === deviceUser.roleId)?.name || 'Role not assigned'}
              </span>
            </li>
          );
        })}
      </ul>
    );
  };

  const updateFormField = (field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined, general: undefined }));
  };

  const updatePasswordField = (field, value) => {
    setPasswordState((prev) => ({
      ...prev,
      [field]: value,
    }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined, general: undefined }));
  };

  const validateCommonFields = () => {
    const errors = {};
    if (!formState.username.trim()) {
      errors.username = 'Required';
    }
    if (!formState.roleId) {
      errors.roleId = 'Required';
    }
    return errors;
  };

  const handleSave = async () => {
    if (!serial || !canModify || saving) {
      return;
    }

    const baseErrors = validateCommonFields();

    if (isNewUser) {
      if (!passwordState.newPassword) {
        baseErrors.newPassword = 'Required';
      } else if (newPasswordValidationMessage) {
        baseErrors.newPassword = newPasswordValidationMessage;
      }
      if (!passwordState.confirmPassword) {
        baseErrors.confirmPassword = 'Required';
      } else if (passwordState.newPassword !== passwordState.confirmPassword) {
        baseErrors.confirmPassword = 'Does not match';
      }

      if (Object.keys(baseErrors).length) {
        setFormErrors(baseErrors);
        return;
      }

      const payload = {
        username: formState.username.trim(),
        roleId: Number(formState.roleId),
        password: passwordState.newPassword,
        confirmPass: passwordState.confirmPassword,
      };

      try {
        setSaving(true);
        const { data } = await axios.post(`/v1/devices/${serial}/users`, payload, {
          headers: authHeaders,
        });
        NotificationManager.success('Device user created successfully.');
        const createdUser = data?.data;
        await fetchUsers();
        if (createdUser?.id) {
          setSelectedUserId(String(createdUser.id));
        } else {
          setSelectedUserId(null);
        }
      } catch (error) {
        console.error(error);
        const message = error?.response?.data?.message || 'Failed to create device user.';
        setFormErrors({ general: message });
        NotificationManager.alert(message);
      } finally {
        setSaving(false);
      }
      return;
    }

    if (Object.keys(baseErrors).length) {
      setFormErrors(baseErrors);
      return;
    }

    if (!initialFormState) {
      setFormErrors({ general: 'Select a user to update.' });
      return;
    }

    const payload = {};

    if (formState.username !== initialFormState.username) {
      payload.username = formState.username.trim();
    }
    if (formState.roleId !== initialFormState.roleId) {
      payload.roleId = Number(formState.roleId);
    }

    if (Object.keys(payload).length === 0) {
      setFormErrors({ general: 'No changes detected.' });
      return;
    }

    payload.id = selectedUser?.id;

    try {
      setSaving(true);
      await axios.put(`/v1/devices/${serial}/users/${selectedUser.id}`, payload, {
        headers: authHeaders,
      });
      NotificationManager.success('Device user updated successfully.');
      await fetchUsers();
    } catch (error) {
      console.error(error);
      const message = error?.response?.data?.message || 'Failed to update device user.';
      setFormErrors({ general: message });
      NotificationManager.alert(message);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordUpdate = async () => {
    if (!serial || !selectedUser || !canModify || savingPassword) {
      return;
    }

    const errors = {};

    if (!passwordState.newPassword) {
      errors.newPassword = 'Required';
    } else if (newPasswordValidationMessage) {
      errors.newPassword = newPasswordValidationMessage;
    }
    if (!passwordState.confirmPassword) {
      errors.confirmPassword = 'Required';
    } else if (passwordState.newPassword !== passwordState.confirmPassword) {
      errors.confirmPassword = 'Does not match';
    }

    if (Object.keys(errors).length) {
      setFormErrors((prev) => ({ ...prev, ...errors }));
      return;
    }

    const payload = {
      newPassword: passwordState.newPassword,
    };

    if (passwordState.currentPassword) {
      payload.currentPassword = passwordState.currentPassword;
    }

    try {
      setSavingPassword(true);
      await axios.put(
        `/v1/devices/${serial}/users/${selectedUser.id}/password`,
        payload,
        { headers: authHeaders }
      );
      NotificationManager.success('Device user password updated.');
      setPasswordState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error(error);
      const message = error?.response?.data?.message || 'Failed to update password.';
      setFormErrors({ general: message });
      NotificationManager.alert(message);
    } finally {
      setSavingPassword(false);
    }
  };

  const handleDelete = () => {
    if (!serial || !selectedUser || !canModify) {
      return;
    }

    ModalManager.confirm(
      'This will permanently remove the selected user from the device.',
      'Delete Device User',
      'Delete',
      async (status, close) => {
        if (status) {
          try {
            setSaving(true);
            await axios.delete(`/v1/devices/${serial}/users/${selectedUser.id}`, {
              headers: authHeaders,
            });
            NotificationManager.success('Device user removed successfully.');
            setSelectedUserId(null);
            await fetchUsers();
          } catch (error) {
            console.error(error);
            const message = error?.response?.data?.message || 'Failed to delete device user.';
            setFormErrors({ general: message });
            NotificationManager.alert(message);
          } finally {
            setSaving(false);
          }
        }
        if (close) close();
      }
    );
  };

  const renderDetail = () => {
    if (!canView) {
      return (
        <div style={styles.message}>
          You can view device configuration, but your role does not allow managing device users.
        </div>
      );
    }

    if (!isNewUser && !selectedUser) {
      return (
        <div style={styles.message}>
          Select a user from the list to review their details.
        </div>
      );
    }

    const showSkeleton =
      (loadingUsers && !users.length) ||
      (loadingRoles && !roles.length && !selectedUserId && !isNewUser);

    if (showSkeleton) {
      return <DetailSkeleton />;
    }

    const saveDisabled =
      !canModify ||
      saving ||
      (isNewUser
        ? !formState.username.trim() ||
          !formState.roleId ||
          !passwordState.newPassword ||
          !passwordState.confirmPassword ||
          Boolean(newPasswordValidationMessage)
        : !initialFormState ||
          (formState.username === initialFormState?.username &&
            formState.roleId === initialFormState?.roleId));

    const passwordDisabled =
      !canModify ||
      savingPassword ||
      !passwordState.newPassword ||
      !passwordState.confirmPassword ||
      Boolean(newPasswordValidationMessage) ||
      passwordState.newPassword !== passwordState.confirmPassword;

    return (
      <div style={styles.detailCard}>
        <div style={styles.detailHeader}>
          <h4 style={styles.detailTitle}>
            {isNewUser ? 'Create New Device User' : `Edit ${selectedUser?.username}`}
          </h4>
          <p style={styles.detailSubtitle}>
            Device-local users can authenticate directly with the PDU when network access is unavailable.
          </p>
        </div>

        {formErrors.general ? <div style={styles.errorText}>{formErrors.general}</div> : null}

        <div>
          <TextInput
            label="Username"
            value={formState.username}
            callback={(value) => updateFormField('username', value)}
            errMsg={formErrors.username}
            disabled={!canModify || saving}
          />
          <SelectInput
            label="Role"
            value={formState.roleId}
            options={roleOptions}
            callback={(value) => updateFormField('roleId', value)}
            errMsg={formErrors.roleId}
            disabled={!canModify || saving || loadingRoles || !roles.length}
            defaultSelectText={
              loadingRoles ? 'Loading roles…' : roles.length ? 'Select role' : 'No roles available'
            }
          />
        </div>

        {isNewUser ? (
          <div style={styles.section}>
            <h5 style={styles.detailTitle}>Set Password</h5>
            <p style={styles.detailSubtitle}>
              Define the credentials this device-local user will need when authenticating directly with the PDU.
            </p>
            <TextInput
              label="Password"
              type="password"
              value={passwordState.newPassword}
              callback={(value) => updatePasswordField('newPassword', value)}
              errMsg={formErrors.newPassword}
              disabled={!canModify || saving}
            />
            <TextInput
              label="Confirm Password"
              type="password"
              value={passwordState.confirmPassword}
              callback={(value) => updatePasswordField('confirmPassword', value)}
              errMsg={formErrors.confirmPassword}
              disabled={!canModify || saving}
            />
            <PasswordRequirementsList password={passwordState.newPassword} />
          </div>
        ) : null}

        {!isNewUser && selectedUser ? (
          <div style={styles.infoGrid}>
            <div style={styles.infoBox}>
              <div style={styles.infoLabel}>Created</div>
              <div style={styles.infoValue}>{formatTimestamp(selectedUser.created)}</div>
            </div>
            <div style={styles.infoBox}>
              <div style={styles.infoLabel}>Last Login</div>
              <div style={styles.infoValue}>{formatTimestamp(selectedUser.lastLogin)}</div>
            </div>
            <div style={styles.infoBox}>
              <div style={styles.infoLabel}>Locked</div>
              <div style={styles.infoValue}>{selectedUser.userIsLocked ? 'Yes' : 'No'}</div>
            </div>
          </div>
        ) : null}

        <div style={styles.buttonRow}>
          <button
            type="button"
            className="button"
            onClick={handleSave}
            disabled={saveDisabled}
          >
            {saving ? 'Saving…' : isNewUser ? 'Create User' : 'Save Changes'}
          </button>
          {isNewUser ? (
            <button
              type="button"
              className="button secondary"
              onClick={() => {
                setSelectedUserId(users.length ? String(users[0].id) : null);
              }}
            >
              Cancel
            </button>
          ) : (
            <button
              type="button"
              className="button"
              style={styles.dangerButton}
              onClick={handleDelete}
              disabled={!canModify || saving}
            >
              Delete User
            </button>
          )}
        </div>

        {!isNewUser && selectedUser ? (
          <div style={styles.section}>
            <h5 style={styles.detailTitle}>Update Password</h5>
            <p style={styles.detailSubtitle}>
              Set a new password for this device account. Leave current password blank if it is not required.
            </p>
            <TextInput
              label="Current Password"
              type="password"
              value={passwordState.currentPassword}
              callback={(value) => updatePasswordField('currentPassword', value)}
              disabled={!canModify || savingPassword}
            />
            <TextInput
              label="New Password"
              type="password"
              value={passwordState.newPassword}
              callback={(value) => updatePasswordField('newPassword', value)}
              errMsg={formErrors.newPassword}
              disabled={!canModify || savingPassword}
            />
            <TextInput
              label="Confirm New Password"
              type="password"
              value={passwordState.confirmPassword}
              callback={(value) => updatePasswordField('confirmPassword', value)}
              errMsg={formErrors.confirmPassword}
              disabled={!canModify || savingPassword}
            />
            <PasswordRequirementsList password={passwordState.newPassword} />
            <div style={styles.buttonRow}>
              <button
                type="button"
                className="button"
                onClick={handlePasswordUpdate}
                disabled={passwordDisabled}
              >
                {savingPassword ? 'Updating…' : 'Update Password'}
              </button>
              <button
                type="button"
                className="button secondary"
                onClick={() => {
                  setPasswordState({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  setFormErrors((prev) => ({ ...prev, newPassword: undefined, confirmPassword: undefined }));
                }}
                disabled={savingPassword}
              >
                Clear
              </button>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  if (!serial) {
    return (
      <div style={styles.message}>
        Device serial number not available. Connect to a device to manage local users.
      </div>
    );
  }

  return (
    <div style={styles.wrapper}>
      <div style={styles.header}>
        <h3 style={styles.title}>{section.label}</h3>
        <p style={styles.description}>{section.description}</p>
      </div>
      {deviceOffline ? (
        <div className="callout warning" style={{ marginBottom: '16px' }}>
          {offlineReason || 'Device is offline. Local user management is read-only until the device reconnects.'}
        </div>
      ) : null}
      {usersLoadError ? <div style={styles.errorText}>{usersLoadError}</div> : null}
      {rolesLoadError ? <div style={styles.errorText}>{rolesLoadError}</div> : null}
      <div style={styles.content}>
        <aside style={styles.listCard}>
          <div style={styles.listHeader}>
            <h4 style={styles.listTitle}>Device Users</h4>
          </div>
          <div
            style={{
              ...styles.createButton,
              ...(!canModify ? styles.createButtonDisabled : {}),
            }}
            onClick={() => canModify && setSelectedUserId('new')}
          >
            {canModify ? 'Create Device User' : deviceOffline ? 'Device Offline' : 'View Only'}
          </div>
          {listContent()}
        </aside>
        <section>{renderDetail()}</section>
      </div>
    </div>
  );
}
